import React from 'react'
import { scaleLinear, scalePow } from 'd3-scale'

import { COLORS } from './constants'
import './ContactTracing.css'
import { range } from 'd3-array'

export enum Mode {
  disabled = 'disabled',
  healthy = 'healthy',
  infected = 'infected',
  infectious = 'infectious',
  symptomatic = 'symptomatic',
  player = 'player',
  quarantinedInfectious = 'quarantinedInfectious',
  quarantinedInfectious2 = 'quarantinedInfectious2',
  quarantinedSymtomatic = 'quarantinedSymtomatic',
  quarantinedInfected = 'quarantinedInfected',
  quarantinedHealthy = 'quarantinedHealthy',
  closeContact = 'closeContact',
  quarantined = 'quarantined'
}

export type Cell = {
  id: string
  row: number
  col: number
  mode: Mode
  x: number
  y: number
  contacts: { [id: string]: number }
}

type Config = {
  // endMode: Mode
  title: string
  grid: Cell[]
  infected?: string[]
  playerId: string
  waypoint?: number
  waypointModes?: {
    id: string
    mode: Mode
  }[]
  moveInfectious?: boolean
  setGlobalInfected?: (cellIds: string[]) => void
  transitions?: {
    progress: number
    from: Mode
    to: Mode
  }[]
  player?: string
  family?: string
  renderBackground?: boolean
}

type Props = {
  width: number
  height: number
  progress: number
  config: Config
}

export const renderCell = (
  c: { mode: Mode; id?: string },
  size: number,
  highlightId?: string
) => (
  <g>
    {[
      Mode.quarantinedInfectious,
      Mode.quarantinedInfectious2,
      Mode.quarantinedSymtomatic,
      Mode.quarantinedInfected,
      Mode.quarantinedHealthy,
      Mode.quarantined
    ].includes(c.mode) && (
      <rect
        x={-(size * 0.9) / 2}
        y={-(size * 0.9) / 2}
        width={size * 0.9}
        height={size * 0.9}
        fill='#fff'
        stroke={COLORS.player}
        strokeWidth={1}
      />
    )}
    {[
      Mode.symptomatic,
      Mode.infectious,
      Mode.quarantinedInfectious,
      Mode.quarantinedInfectious2,
      Mode.quarantinedSymtomatic
    ].includes(c.mode) &&
      [
        [1, 0],
        [0.7, -0.7],
        [0, -1],
        [-0.7, -0.7],
        [-1, 0],
        [-0.7, 0.7],
        [0, 1],
        [0.7, 0.7]
      ].map(([x, y], i) => (
        <circle
          key={i}
          cx={(x * size) / 4.8}
          cy={(y * size) / 4.8}
          r={size / 20}
          fill={COLORS[c.mode]}
        />
      ))}
    <circle r={size / 6} fill={COLORS[c.mode]} />
    <circle
      r={size / 2.5}
      fill={COLORS[c.mode]}
      opacity={0.5}
      strokeWidth={c.id && c.id === highlightId ? 2 : 0}
      stroke={COLORS.player}
    />
  </g>
)

const ContactTracing = ({ width, height, progress, config }: Props) => {
  const {
    title,
    waypoint = 1,
    waypointModes = [],
    playerId,
    infected = [],
    transitions = [],
    setGlobalInfected,
    moveInfectious,
    family = '0-3',
    renderBackground,
    player = '0-4'
  } = config

  const gridRows = 14
  const gridCols = 9
  const gridSize = width / gridCols
  const canvasSize = {
    width: gridCols * gridSize,
    height: gridRows * gridSize
  }

  const [active, setActive] = React.useState(false)

  React.useEffect(() => setActive(progress > 0 && progress < 1), [progress])

  const speedScale = React.useMemo(() => {
    const scale = scalePow()
      .domain([-waypoint, 0, 0.2, 1 - waypoint])
      .range([0, 0, 0.2, 0])
    scale.clamp(true)
    return scale
  }, [waypoint])

  const playerPosFn = (progress: number) => {
    const realProgress = progress - speedScale(progress - waypoint)

    const playerPos = {
      x:
        (canvasSize.width / 2 - gridSize / 2) *
        (1 + 0.5 * Math.sin(2 * Math.PI * realProgress)),
      y: canvasSize.height * realProgress
    }
    return playerPos
  }

  const [grid, setGrid] = React.useState<Cell[]>(
    [...config.grid.reverse()]
      .filter(c => c.mode !== Mode.disabled && c.id !== playerId)
      .map(c => ({
        ...c,
        x: c.col * gridSize,
        y: c.row * gridSize
      }))
      .concat(
        config.grid
          .filter(c => c.id === playerId)
          .map(c => ({ ...c, ...playerPosFn(0) }))
      )
  )

  // React.useEffect(() => {
  //   if (waypoint && progress < waypoint) {
  //     setGrid(
  //       [...config.grid.reverse()]
  //         .filter(c => c.mode !== Mode.disabled && c.id !== playerId)
  //         .map(c => ({
  //           ...c,
  //           x: c.col * gridSize,
  //           y: c.row * gridSize
  //         }))
  //         .concat(
  //           config.grid
  //             .filter(c => c.id === playerId)
  //             .map(c => ({ ...c, ...playerPosFn(0) }))
  //         )
  //     )
  //   }
  // }, [progress])

  const updateGameState = React.useCallback(() => {
    if (active)
      setGrid(grid => {
        return grid.map(c => {
          // find cells nearby right now
          const close: [string, number][] = grid
            .filter(
              g =>
                g.id !== c.id &&
                Math.abs(g.x - c.x) < gridSize / 2 &&
                Math.abs(g.y - c.y) < gridSize / 2
            )
            .map(c => [c.id, 1])
          // sum up all contacts
          const contacts = Object.entries(c.contacts || {})
            .concat(close)
            .reduce<{ [id: string]: number }>((acc, [id, count]) => {
              acc[id] = (acc[id] || 0) + count
              return acc
            }, {})

          // find close contacts with more than 2 counts
          const applyOnContact = Object.entries(contacts)
            .filter(([id, count]) => count > 1)
            .some(([id]) =>
              grid.find(
                g =>
                  g.id === id &&
                  (c.mode === Mode.healthy || c.mode === Mode.player) &&
                  (g.mode === Mode.infectious || g.mode === Mode.symptomatic)
              )
            )

          // apply mode change if necessary
          return {
            ...c,
            mode: applyOnContact ? Mode.infected : c.mode,
            contacts
          }
        })
      })
  }, [active])

  React.useEffect(() => {
    if (config.infected && config.infected.length > 0)
      setGrid(grid =>
        grid.map(c =>
          infected.includes(c.id) ? { ...c, mode: Mode.infectious } : c
        )
      )
  }, [config.infected])

  React.useEffect(() => {
    if (active) {
      const job = setInterval(updateGameState, 500)
      return () => clearInterval(job)
    }
  }, [active])

  const rotationScale = React.useMemo(() => {
    const scale = scaleLinear()
      .domain([0, 0.1, 1])
      .range([0, 0, 2 * Math.PI])
    scale.clamp(true)
    return scale
  }, [])

  const cellPosFn = (progress: number, cell: Cell) => {
    const angle = rotationScale(progress)
    const centerX = cell.col * gridSize
    const centerY = cell.row * gridSize + gridSize
    const pointX = centerX
    const pointY = centerY - gridSize
    const x1 = pointX - centerX
    const y1 = pointY - centerY
    const s = 1.5 * Math.sin(angle)
    const c = Math.cos(angle)
    return {
      x: x1 * c - y1 * s + centerX,
      y: x1 * s + y1 * c + centerY
    }
  }

  React.useEffect(() => {
    if (active) {
      setGrid(grid => {
        return grid
          .map(c =>
            c.id === playerId
              ? {
                  ...c,
                  ...([
                    Mode.symptomatic,
                    Mode.quarantinedInfectious,
                    Mode.quarantinedSymtomatic
                  ].includes(c.mode)
                    ? playerPosFn(0)
                    : playerPosFn(progress))
                }
              : c
          )
          .map(c =>
            moveInfectious && c.id !== family && c.mode === Mode.infectious
              ? { ...c, ...cellPosFn(progress, c) }
              : c
          )
          .map(c => {
            if (transitions.length > 0) {
              const t1 = transitions.find(
                t => progress > t.progress && t.from === c.mode
              )
              const t2 = transitions.find(
                t => progress <= t.progress && t.to === c.mode
              )

              return t1 || t2 ? { ...c, mode: t1?.to || t2?.from } : c
            } else {
              return c
            }
          })
          .map(c => {
            if (progress > waypoint + 0.05) {
              const wpMode = waypointModes.find(m => m.id === c.id)
              if (wpMode) {
                return { ...c, mode: wpMode.mode }
              } else {
                return c
              }
            } else {
              return c
            }
          })
      })
      setGlobalInfected &&
        setGlobalInfected(
          grid
            .filter(c => c.id !== family && c.mode === Mode.infected)
            .map(c => c.id)
        )
    }
  }, [progress, active])

  return (
    <>
      {/* <div style={{ position: 'absolute', top: 0 }}> {progress.toFixed(2)}</div> */}
      <div
        style={{
          width,
          height,
          background: COLORS.bg,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <svg width={canvasSize.width} height={canvasSize.height}>
          <g>
            <rect
              x={canvasSize.width / 3 - gridSize / 20}
              y={0}
              width={canvasSize.width / 3 + gridSize / 20}
              height={gridSize}
              fill={COLORS.home}
              shapeRendering='crispEdges'
            />
            <g transform={`translate(${gridSize / 2},${gridSize / 2})`}>
              {grid
                .filter(c => c.mode !== Mode.disabled)
                .map((c, i) => (
                  <g key={i} transform={`translate(${c.x},${c.y})`}>
                    {renderCell(c, gridSize, player)}
                    {/* <circle r={gridSize / 3} fill={COLORS[c.mode]} /> */}
                    {/* <text fill='#fff' textAnchor='middle'>
                      {c.id}
                    </text> */}
                  </g>
                ))}
            </g>
            {renderBackground &&
              range(20).map(i => (
                <g
                  key={i}
                  transform={`translate(${canvasSize.width / 2},${-200})`}
                >
                  <circle
                    r={
                      progress *
                        (1 + 250 / canvasSize.height) *
                        canvasSize.height +
                      i * 10
                    }
                    fill='none'
                    stroke='#0077fb'
                  />
                </g>
              ))}
          </g>
        </svg>
      </div>
    </>
  )
}

export default ContactTracing
